import { observer } from 'mobx-react-lite'
import { type DocumentsListViewModel } from '@/features/documents/DocumentsListViewModel'
import { type Document } from '@/features/documents/Document'
import { type DocumentViewModel } from '@/features/documents/DocumentViewModel'
import { InfiniteScrollLoaderContainer } from '@/components/InfiniteScrollLoader/InfiniteScrollLoaderContainer'
import { type InfiniteScrollProvider } from '@/infra/InfiniteScrollProvider'
import {
  DocumentsList,
  DocumentsTable,
  DocumentsTableRow,
} from '@/features/documents/components/DocumentRoot/DocumentsList'

export interface DocumentsListContainerProps {
  vm: DocumentsListViewModel
  scrollProvider: InfiniteScrollProvider<Document, Record<string, unknown>>
  onQueueFilesUpload: (files: FileList) => void
  canUploadFiles: boolean
}

export const DocumentsListContainer = observer(function DocumentsListContainer({
  vm,
  scrollProvider,
  onQueueFilesUpload,
  canUploadFiles,
}: DocumentsListContainerProps) {
  return (
    <DocumentsList
      searchText={vm.searchText}
      onSearchTextChange={vm.setSearchText}
      onQueueFilesUpload={onQueueFilesUpload}
      canUploadFiles={canUploadFiles}
      table={<DocumentsTableContainer vm={vm} />}
      scrollLoader={
        <InfiniteScrollLoaderContainer scrollProvider={scrollProvider} />
      }
    />
  )
})

interface DocumentsTableContainerProps {
  vm: DocumentsListViewModel
}

const DocumentsTableContainer = observer(function DocumentsTableContainer({
  vm,
}: DocumentsTableContainerProps) {
  return (
    <DocumentsTable columns={vm.columnHeaders} onSort={vm.setSort}>
      {vm.documentVms.map((documentVm) => (
        <DocumentsTableRowContainer
          key={documentVm.document.id}
          vm={documentVm}
          showWorkspace={vm.showWorkspace}
        />
      ))}
    </DocumentsTable>
  )
})

interface DocumentsTableRowContainerProps {
  vm: DocumentViewModel
  showWorkspace: boolean
}

const DocumentsTableRowContainer = observer(
  function DocumentsTableRowContainer({
    vm,
    showWorkspace,
  }: DocumentsTableRowContainerProps) {
    return (
      <DocumentsTableRow
        title={vm.document.title}
        archived={vm.document.archived}
        isPurged={vm.document.isPurged}
        author={vm.author}
        workspaceName={showWorkspace ? vm.document.workspace.name : undefined}
        documentTypeName={vm.document.documentType?.name}
        fileType={vm.document.fileType}
        dateModified={vm.document.dateModified}
        uploadPercent={vm.uploadPercent}
        isProcessing={vm.document.isProcessing}
        rejection={vm.rejection}
        downloadable={vm.document.downloadable}
        onView={vm.view}
        onDownload={vm.download}
        onDelete={vm.delete}
      />
    )
  },
)
